import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Search, 
  Filter,
  User,
  Shield,
  Crown,
  Hash,
  UserCheck,
  UserX,
  ChevronDown,
  ChevronUp,
  Eye,
  MoreVertical,
  Edit,
  Lock,
  Trash2,
  X
} from 'lucide-react';
import { userAPI, timesheetAPI, projectAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const EmployeeList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [nameSearch, setNameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [employeeCodeSearch, setEmployeeCodeSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(''); // 'EMPLOYEE', 'MANAGER', or ''
  const [projectFilter, setProjectFilter] = useState(''); // Project name filter
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [userProjects, setUserProjects] = useState({}); // { userId: [projects] }
  const [allProjectsList, setAllProjectsList] = useState([]); // All available projects
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedUserForProjects, setSelectedUserForProjects] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [passwordData, setPasswordData] = useState({ password: '', confirmPassword: '' });
  const dropdownRefs = useRef({});
  const buttonRefs = useRef({});

  // Fetch employees and managers based on user role
  const { data: employees = [], isLoading: employeesLoading } = useQuery(
    'all-employees-list',
    () => {
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.getAllEmployees();
      } else {
        return userAPI.getManagersAndEmployeesAdmin();
      }
    },
    {
      enabled: !!user,
      select: (response) => {
        if (response?.data?.data) {
          return response.data.data;
        } else if (response?.data) {
          return response.data;
        } else {
          return [];
        }
      },
      onError: (error) => {
        console.error('Error fetching employees:', error);
        toast.error('Failed to fetch employees: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Fetch managers separately for super admin
  const { data: managers = [], isLoading: managersLoading } = useQuery(
    'all-managers-list',
    () => {
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.getAllManagers();
      } else {
        return userAPI.getAllManagersAdmin();
      }
    },
    {
      enabled: !!user && user?.role === 'SUPER_ADMIN',
      select: (response) => {
        if (response?.data?.data) {
          return response.data.data;
        } else if (response?.data) {
          return response.data;
        } else {
          return [];
        }
      },
      onError: (error) => {
        console.error('Error fetching managers:', error);
        toast.error('Failed to fetch managers: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Combine employees and managers
  const allUsers = user?.role === 'SUPER_ADMIN' 
    ? [...(managers || []), ...(employees || [])]
    : (employees || []);


  // Fetch assigned projects for all users
  useEffect(() => {
    const fetchProjectsForUsers = async () => {
      if (allUsers.length === 0) return;

      const projectsData = {};

      try {
        // Fetch all projects first
        const allProjectsResponse = await projectAPI.getAllProjects();
        const allProjects = allProjectsResponse?.data?.projects || allProjectsResponse?.data || [];
        
        // Store all projects for the filter dropdown
        const uniqueProjects = [...new Set(allProjects.map(p => p.name).filter(Boolean))];
        setAllProjectsList(uniqueProjects.sort());

        // For each user, find their assigned projects
        allUsers.forEach((userItem) => {
          const roleValue = typeof userItem.role === 'object' ? userItem.role?.name : userItem.role;
          const userProjectsList = [];

          allProjects.forEach((project) => {
            // Check if user is assigned as employee
            if (project.assignedEmployees && Array.isArray(project.assignedEmployees)) {
              const isEmployeeAssigned = project.assignedEmployees.some(
                emp => emp.id === userItem.id
              );
              if (isEmployeeAssigned) {
                userProjectsList.push(project.name);
              }
            }

            // Check if user is assigned as manager
            if (roleValue === 'MANAGER' && project.assignedManagers && Array.isArray(project.assignedManagers)) {
              const isManagerAssigned = project.assignedManagers.some(
                mgr => mgr.id === userItem.id
              );
              if (isManagerAssigned) {
                userProjectsList.push(project.name);
              }
            }
          });

          projectsData[userItem.id] = userProjectsList;
        });
      } catch (error) {
        console.error('Error fetching projects:', error);
        // Fallback: try to fetch individual employee details
        const employeeUsers = allUsers.filter(u => {
          const roleValue = typeof u.role === 'object' ? u.role?.name : u.role;
          return roleValue === 'EMPLOYEE';
        });
        
        const fallbackPromises = employeeUsers.map(async (userItem) => {
          try {
            const response = await userAPI.getEmployeeDetails(userItem.id);
            if (response?.data?.projects) {
              projectsData[userItem.id] = response.data.projects.map(p => p.name);
            } else {
              projectsData[userItem.id] = [];
            }
          } catch (err) {
            console.error(`Error fetching projects for user ${userItem.id}:`, err);
            projectsData[userItem.id] = [];
          }
        });
        
        await Promise.all(fallbackPromises);
      } finally {
        setUserProjects(projectsData);
      }
    };

    fetchProjectsForUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees?.length, managers?.length, user?.role]);

  // Calculate data for each user
  const userData = useMemo(() => {
    return allUsers.map((userItem) => {
      const roleValue = typeof userItem.role === 'object' ? userItem.role?.name : userItem.role;

      // Get projects from assigned projects
      const assignedProjects = userProjects[userItem.id] || [];
      const projects = assignedProjects.length > 0 ? assignedProjects : ['No projects'];

      return {
        ...userItem,
        role: roleValue,
        projects: projects,
        isLoading: false
      };
    });
  }, [allUsers, userProjects]);

  // Filter users based on search terms, role filter, and project filter
  const filteredUsers = userData.filter(userItem => {
    const matchesName = !nameSearch || 
      userItem.firstName?.toLowerCase().includes(nameSearch.toLowerCase()) ||
      userItem.lastName?.toLowerCase().includes(nameSearch.toLowerCase());

    const matchesEmail = !emailSearch || 
      userItem.email?.toLowerCase().includes(emailSearch.toLowerCase());

    const matchesEmployeeCode = !employeeCodeSearch || 
      userItem.employeeCode?.toLowerCase().includes(employeeCodeSearch.toLowerCase());

    const matchesRole = !roleFilter || userItem.role === roleFilter;

    return matchesName && matchesEmail && matchesEmployeeCode && matchesRole;
  });

  // Sort users
  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;

    return [...filteredUsers].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'name') {
        aValue = `${a.firstName} ${a.lastName}`;
        bValue = `${b.firstName} ${b.lastName}`;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'ADMIN':
        return <Shield className="w-4 h-4 text-blue-500" />;
      case 'MANAGER':
        return <Users className="w-4 h-4 text-purple-500" />;
      case 'EMPLOYEE':
        return <User className="w-4 h-4 text-green-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      'SUPER_ADMIN': 'bg-yellow-100 text-yellow-800',
      'ADMIN': 'bg-blue-100 text-blue-800',
      'MANAGER': 'bg-purple-100 text-purple-800',
      'EMPLOYEE': 'bg-green-100 text-green-800'
    };
    return badges[role] || 'bg-gray-100 text-gray-800';
  };

  // Mutations
  const editMutation = useMutation(
    ({ userId, userData }) => {
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.updateUser(userId, userData);
      } else {
        return userAPI.updateUserAdmin(userId, userData);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('all-employees-list');
        queryClient.invalidateQueries('all-managers-list');
        toast.success('User updated successfully');
        setShowEditModal(false);
        setSelectedUser(null);
        setEditFormData({});
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user');
      }
    }
  );

  const passwordMutation = useMutation(
    ({ userId, password }) => {
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.updateUserPassword(userId, { password });
      } else {
        return userAPI.updateUserPasswordAdmin(userId, { password });
      }
    },
    {
      onSuccess: () => {
        toast.success('Password updated successfully');
        setShowPasswordModal(false);
        setSelectedUser(null);
        setPasswordData({ password: '', confirmPassword: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update password');
      }
    }
  );

  const deleteMutation = useMutation(
    (userId) => {
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.deleteUser(userId);
      } else {
        return userAPI.deleteEmployee(userId);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('all-employees-list');
        queryClient.invalidateQueries('all-managers-list');
        toast.success('User deactivated successfully');
        setShowDeleteModal(false);
        setSelectedUser(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete user');
      }
    }
  );

  // Handle dropdown toggle
  const toggleDropdown = (userId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (openDropdown === userId) {
      setOpenDropdown(null);
    } else {
      const buttonElement = event?.currentTarget || buttonRefs.current[userId];
      
      if (buttonElement) {
        const rect = buttonElement.getBoundingClientRect();
        // For fixed positioning, use viewport coordinates directly (no scroll offset needed)
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left + (rect.width / 2)
        });
      }
      
      setOpenDropdown(userId);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (openDropdown === null) return;

    const handleClickOutside = (event) => {
      const dropdownRef = dropdownRefs.current[openDropdown];
      const buttonRef = buttonRefs.current[openDropdown];
      
      const clickedInsideDropdown = dropdownRef && dropdownRef.contains(event.target);
      const clickedOnButton = buttonRef && buttonRef.contains(event.target);
      
      if (!clickedInsideDropdown && !clickedOnButton) {
        setOpenDropdown(null);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [openDropdown]);

  // Handle actions
  const handleEdit = (userItem) => {
    setSelectedUser(userItem);
    setEditFormData({
      firstName: userItem.firstName || '',
      lastName: userItem.lastName || '',
      email: userItem.email || '',
      username: userItem.username || '',
      employeeCode: userItem.employeeCode || ''
    });
    setShowEditModal(true);
    setOpenDropdown(null);
  };

  const handleChangePassword = (userItem) => {
    setSelectedUser(userItem);
    setPasswordData({ password: '', confirmPassword: '' });
    setShowPasswordModal(true);
    setOpenDropdown(null);
  };

  const handleViewTimesheet = (userItem) => {
    const userRole = typeof user?.role === 'object' ? user?.role?.name : user?.role;
    const viewTimesheetUrl = userRole === 'SUPER_ADMIN' 
      ? `/super-admin/users/${userItem.id}/timesheets`
      : `/admin/users/${userItem.id}/timesheets`;
    navigate(viewTimesheetUrl);
    setOpenDropdown(null);
  };

  const handleDelete = (userItem) => {
    setSelectedUser(userItem);
    setShowDeleteModal(true);
    setOpenDropdown(null);
  };

  const handleEditSubmit = () => {
    if (!selectedUser) return;
    
    if (!editFormData.firstName || !editFormData.lastName || !editFormData.email || !editFormData.username) {
      toast.error('Please fill in all required fields');
      return;
    }

    editMutation.mutate({
      userId: selectedUser.id,
      userData: editFormData
    });
  };

  const handlePasswordSubmit = () => {
    if (!selectedUser) return;

    if (!passwordData.password || !passwordData.confirmPassword) {
      toast.error('Please fill in both password fields');
      return;
    }

    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    passwordMutation.mutate({
      userId: selectedUser.id,
      password: passwordData.password
    });
  };

  const handleDeleteConfirm = () => {
    if (!selectedUser) return;
    deleteMutation.mutate(selectedUser.id);
  };

  const isLoading = employeesLoading || (user?.role === 'SUPER_ADMIN' && managersLoading);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full mx-auto px-0">
          <div className="flex justify-between items-center py-6 px-4 sm:px-6 lg:px-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Employee & Manager List</h1>
              <p className="text-gray-600">View all employees and managers with project assignments and working hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="w-full mx-auto px-0 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Name Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name..."
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {/* Email Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email..."
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {/* Employee Code Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by employee code..."
                value={employeeCodeSearch}
                onChange={(e) => setEmployeeCodeSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {/* Filter Dropdowns */}
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Roles</option>
                <option value="EMPLOYEE">Employees</option>
                <option value="MANAGER">Managers</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 relative"
        >
          <div className="overflow-x-auto" style={{ overflowY: 'visible' }}>
            <table className="min-w-full divide-y divide-gray-200" style={{ position: 'relative' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    S.No
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Name</span>
                      {sortConfig.key === 'name' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Project
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 relative">
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No users found</p>
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map((userItem, index) => {
                    const userRole = typeof user?.role === 'object' ? user?.role?.name : user?.role;
                    const viewTimesheetUrl = userRole === 'SUPER_ADMIN' 
                      ? `/super-admin/users/${userItem.id}/timesheets`
                      : `/admin/users/${userItem.id}/timesheets`;
                    
                    return (
                      <tr key={userItem.id} className="hover:bg-gray-50 transition-colors relative">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <button
                              onClick={() => handleViewTimesheet(userItem)}
                              className="text-sm font-medium text-black-700 hover:text-blue-900 cursor-pointer transition-colors text-left underline"
                            >
                              {userItem.firstName} {userItem.lastName}
                            </button>
                            <div className="text-sm text-gray-500">{userItem.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {getRoleIcon(userItem.role)}
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getRoleBadge(userItem.role)}`}>
                              {userItem.role}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => {
                              setSelectedUserForProjects(userItem);
                              setShowProjectModal(true);
                            }}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer transition-colors underline"
                          >
                            View Project
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${
                            userItem.isActive 
                              ? 'text-green-600' 
                              : 'text-red-600'
                          }`}>
                            {userItem.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium relative">
                          <div className="relative inline-block" ref={el => dropdownRefs.current[userItem.id] = el}>
                            <button
                              type="button"
                              ref={el => {
                                if (el) {
                                  buttonRefs.current[userItem.id] = el;
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                toggleDropdown(userItem.id, e);
                              }}
                              className="inline-flex items-center justify-center p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-5 h-5" />
                            </button>
                            
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Dropdown Portal */}
      {openDropdown && sortedUsers.find(u => u.id === openDropdown) && dropdownPosition.top > 0 && (() => {
        const userItem = sortedUsers.find(u => u.id === openDropdown);
        if (!userItem) return null;
        
        return createPortal(
          <div
            className="fixed w-48 bg-white rounded-md shadow-xl border border-gray-200"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              transform: 'translateX(-50%)',
              zIndex: 10000
            }}
            ref={el => {
              if (el && openDropdown) {
                dropdownRefs.current[openDropdown] = el;
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="py-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(userItem);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Employee
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangePassword(userItem);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewTimesheet(userItem);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Timesheet
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(userItem);
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Project Modal */}
      {showProjectModal && selectedUserForProjects && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Projects - {selectedUserForProjects.firstName} {selectedUserForProjects.lastName}
              </h3>
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setSelectedUserForProjects(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {selectedUserForProjects.projects && selectedUserForProjects.projects.length > 0 && selectedUserForProjects.projects[0] !== 'No projects' ? (
                <div className="space-y-2">
                  {selectedUserForProjects.projects.map((project, idx) => (
                    <div
                      key={idx}
                      className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 mr-2 mb-2"
                    >
                      {project}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No projects assigned</p>
              )}
            </div>
            <div className="flex justify-end p-6 border-t">
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setSelectedUserForProjects(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Edit Employee</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                  setEditFormData({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={editFormData.firstName}
                  onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={editFormData.lastName}
                  onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                <input
                  type="text"
                  value={editFormData.username}
                  onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee Code</label>
                <input
                  type="text"
                  value={editFormData.employeeCode}
                  onChange={(e) => setEditFormData({ ...editFormData, employeeCode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                  setEditFormData({});
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={editMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {editMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedUser(null);
                  setPasswordData({ password: '', confirmPassword: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Changing password for: <span className="font-medium">{selectedUser.firstName} {selectedUser.lastName}</span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                <input
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedUser(null);
                  setPasswordData({ password: '', confirmPassword: '' });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordSubmit}
                disabled={passwordMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {passwordMutation.isLoading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to deactivate <span className="font-medium">{selectedUser.firstName} {selectedUser.lastName}</span>?
              </p>
              <p className="text-xs text-gray-500">
                This will deactivate the user account. All related data will be preserved.
              </p>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;
